import axios, { type AxiosInstance, AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { env } from '@/config/env';
import { AUTH_STORAGE_KEYS } from '@features/authentication/types';

/**
 * API Client configuration
 */
const API_BASE_URL = env.apiUrl;
const API_TIMEOUT = 30000; // 30 seconds

/**
 * Flag to prevent multiple refresh attempts
 */
let isRefreshing = false;
let failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}[] = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Clear all auth tokens and redirect to login
 */
const clearAuthAndRedirect = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(AUTH_STORAGE_KEYS.EXPIRES_AT);
  window.location.href = '/login';
};

/**
 * Create configured axios instance
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    internalSystemToken: env.internalSystemToken,
  } as Record<string, string>,
});

/**
 * Request interceptor
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add request ID for tracing
    config.headers['X-Request-ID'] = crypto.randomUUID();

    // Add timestamp
    config.headers['X-Request-Time'] = new Date().toISOString();

    // Add authorization token if available
    const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor with automatic token refresh
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const isLoginRequest = originalRequest.url?.includes('/auth/login') ?? false;
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh') ?? false;
    const isOnLoginPage = window.location.pathname === '/login';

    // Handle 403 Forbidden (blocked user) - always logout
    if (error.response?.status === 403) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // Don't retry for login or refresh requests
      if (isLoginRequest || isRefreshRequest || isOnLoginPage) {
        if (isRefreshRequest) {
          // Refresh failed - clear tokens and redirect
          clearAuthAndRedirect();
        }
        return Promise.reject(error);
      }

      // Don't retry if already retried
      if (originalRequest._retry) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // Check if we have a refresh token
      const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token as string}`;
            return apiClient(originalRequest);
          })
          .catch((err: unknown) =>
            Promise.reject(err instanceof Error ? err : new Error(String(err)))
          );
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Call refresh endpoint
        const response = await axios.post<{
          accessToken: string;
          expiresIn: number;
          refreshToken: string;
          refreshExpiresIn: number;
        }>(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
              internalSystemToken: env.internalSystemToken,
            },
          }
        );

        const { accessToken, expiresIn, refreshToken: newRefreshToken } = response.data;

        // Store new tokens
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
        localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        const expiresAt = Date.now() + expiresIn * 1000;
        localStorage.setItem(AUTH_STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());

        // Update authorization header for original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue(null, accessToken);

        // Retry original request
        return await apiClient(originalRequest);
      } catch (refreshError: unknown) {
        // Refresh failed - clear tokens and redirect
        const errorToProcess =
          refreshError instanceof Error ? refreshError : new Error(String(refreshError));
        processQueue(errorToProcess, null);
        clearAuthAndRedirect();
        return await Promise.reject(errorToProcess);
      } finally {
        isRefreshing = false;
      }
    }

    return await Promise.reject(error);
  }
);

/**
 * API Error class
 */
export class APIError extends Error {
  public statusCode: number;
  public code: string;
  public details?: unknown;

  constructor(message: string, statusCode: number, code: string, details?: unknown) {
    super(message);
    this.name = 'APIError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
  }
}

/**
 * Handle API errors
 */
export const handleAPIError = (error: unknown): APIError => {
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status ?? 500;
    const responseData = error.response?.data as
      | { code?: string; error?: string; message?: string; details?: unknown; statusCode?: number }
      | undefined;
    const code = responseData?.code ?? responseData?.error ?? 'UNKNOWN_ERROR';
    const message = responseData?.message ?? 'Ocorreu um erro inesperado. Tente novamente.';
    const details = responseData?.details;

    return new APIError(message, statusCode, code, details);
  }

  return new APIError('Ocorreu um erro inesperado. Tente novamente.', 500, 'UNKNOWN_ERROR');
};
